import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import StatusPill from "../components/StatusPill";
import {
  STATUS_OPTIONS, FLEA_TOTAL_SLOTS, FLEA_BASE_LAKHS, FLEA_RACK_LAKHS,
} from "../lib/constants";
import { formatLakhs, formatPercent, formatDateShort } from "../lib/format";
import { exportCSV } from "../lib/csv";
import { Plus, Pencil, Check, X, Trash2, Download, Store } from "lucide-react";

const EMPTY = { vendor_name: "", slots_taken: 1, status: "prospect", closing_price_lakhs: null, notes: "" };

function effectiveLakhs(row) {
  if (!row) return 0;
  const cp = row.closing_price_lakhs;
  if (cp != null && cp !== "" && !Number.isNaN(Number(cp))) return Number(cp);
  return (Number(row.slots_taken) || 0) * FLEA_BASE_LAKHS;
}

function VendorRow({ row, editing, draft, setDraft, onEdit, onCancel, onSave, onDelete }) {
  const slots = editing ? draft.slots_taken : row.slots_taken;
  const rack = slots * FLEA_BASE_LAKHS;
  const cp = editing ? draft.closing_price_lakhs : row.closing_price_lakhs;
  const hasClosing = cp != null && cp !== "" && !Number.isNaN(Number(cp));
  return (
    <tr className="border-t border-zinc-900 hover:bg-[#161616] transition-colors group" data-testid={`flea-row-${row.id}`}>
      <td className="px-4 py-3">
        {editing ? (
          <input
            value={draft.vendor_name}
            onChange={(e) => setDraft({ ...draft, vendor_name: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
            data-testid="vendor-name-input"
          />
        ) : (
          <span className="font-medium">{row.vendor_name}</span>
        )}
      </td>
      <td className="px-4 py-3 tabular">
        {editing ? (
          <input type="number" min={1} max={FLEA_TOTAL_SLOTS}
            value={draft.slots_taken}
            onChange={(e) => setDraft({ ...draft, slots_taken: Math.max(1, Math.min(FLEA_TOTAL_SLOTS, +e.target.value)) })}
            className="w-16 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
        ) : (
          row.slots_taken
        )}
      </td>
      <td className="px-4 py-3 tabular text-zinc-400 text-sm">{formatLakhs(rack)}</td>
      <td className="px-4 py-3 tabular">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input type="number" step="0.05" min={0} max={1000}
              value={draft.closing_price_lakhs == null ? "" : draft.closing_price_lakhs}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") setDraft({ ...draft, closing_price_lakhs: null });
                else setDraft({ ...draft, closing_price_lakhs: Math.max(0, parseFloat(v)) });
              }}
              placeholder={`Rack: ${rack}`}
              title="Closing value in ₹ Lakhs (blank = use rack value)"
              data-testid="flea-closing-input"
              className="w-24 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
            <span className="text-xs text-zinc-500">L</span>
          </div>
        ) : hasClosing ? (
          <div className="leading-tight">
            <div className="text-emerald-400 font-medium">{formatLakhs(Number(cp))}</div>
            <div className="text-[10px] text-zinc-600">closed</div>
          </div>
        ) : (
          <span className="text-zinc-600 text-sm">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            className="bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
            data-testid="flea-status-select">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <StatusPill status={row.status} />
        )}
      </td>
      <td className="px-4 py-3 text-sm text-zinc-400 max-w-[260px]">
        {editing ? (
          <input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
        ) : (
          <span className="line-clamp-1">{row.notes || "—"}</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <button onClick={onSave} data-testid="flea-row-save" className="p-1.5 text-emerald-400 hover:bg-[#1a1a1a]"><Check className="h-4 w-4" /></button>
            <button onClick={onCancel} className="p-1.5 text-zinc-400 hover:bg-[#1a1a1a]"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={onEdit} data-testid="flea-row-edit" className="p-1.5 text-zinc-300 hover:bg-[#1a1a1a]"><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={onDelete} data-testid="flea-row-delete" className="p-1.5 text-red-400 hover:bg-[#1a1a1a]"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function FleaMarket() {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState(EMPTY);
  const [err, setErr] = useState("");

  const reload = async () => {
    const { data } = await api.get("/flea-market");
    setRows(data);
  };
  useEffect(() => { reload(); }, []);

  const closedRows = useMemo(() => rows.filter((r) => r.status === "closed"), [rows]);
  const negotiatingRows = rows.filter((r) => r.status === "negotiating");
  const filledSlots = closedRows.reduce((s, r) => s + r.slots_taken, 0);
  const revenueLakhs = closedRows.reduce((s, r) => s + effectiveLakhs(r), 0);

  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r }); setErr(""); };

  const save = async () => {
    setErr("");
    try {
      await api.put(`/flea-market/${editingId}`, draft);
      setEditingId(null);
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const create = async () => {
    setErr("");
    if (!newRow.vendor_name.trim()) return;
    try {
      await api.post("/flea-market", newRow);
      setNewRow(EMPTY);
      setAdding(false);
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this vendor?")) return;
    await api.delete(`/flea-market/${id}`);
    await reload();
  };

  const onExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    exportCSV(`isf-flea-market-${stamp}.csv`, rows, [
      { label: "Vendor",        accessor: (r) => r.vendor_name },
      { label: "Slots",         accessor: (r) => r.slots_taken },
      { label: "Rack ₹ L",      accessor: (r) => (r.slots_taken * FLEA_BASE_LAKHS).toFixed(2) },
      { label: "Closing ₹ L",   accessor: (r) => (r.closing_price_lakhs ?? "") === "" ? "" : Number(r.closing_price_lakhs).toFixed(2) },
      { label: "Status",        accessor: (r) => r.status },
      { label: "Notes",         accessor: (r) => r.notes || "" },
      { label: "Created",       accessor: (r) => formatDateShort(r.created_at) },
      { label: "Updated",       accessor: (r) => formatDateShort(r.updated_at) },
    ]);
  };

  return (
    <Layout>
      <PageHeader
        overline="Pipeline · marketplace"
        title="Flea Market"
        subtitle={`30 slots @ ₹ 50,000 each — ₹ ${FLEA_RACK_LAKHS} L rack value.`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={onExport}
              disabled={rows.length === 0}
              data-testid="export-flea-csv"
              className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 hover:bg-[#181818] disabled:opacity-40 disabled:cursor-not-allowed text-sm px-4 py-2 transition-colors"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={() => { setAdding(true); setErr(""); }}
              data-testid="add-flea-btn"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-4 py-2 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add vendor
            </button>
          </div>
        }
        testId="flea-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="p-5" testId="kpi-flea-revenue">
          <div className="overline mb-2">Flea revenue closed</div>
          <div className="font-display text-3xl font-bold tabular">{formatLakhs(revenueLakhs)}</div>
          <div className="text-xs text-zinc-500 mt-1">
            of {formatLakhs(FLEA_RACK_LAKHS)} · {formatPercent(revenueLakhs, FLEA_RACK_LAKHS)}
          </div>
          <div className="mt-3 h-1 bg-zinc-900">
            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (revenueLakhs / FLEA_RACK_LAKHS) * 100)}%` }} />
          </div>
        </Card>
        <Card className="p-5" testId="kpi-flea-slots">
          <div className="overline mb-2">Slots filled</div>
          <div className="font-display text-3xl font-bold tabular">
            {filledSlots}<span className="text-zinc-600 text-2xl"> / {FLEA_TOTAL_SLOTS}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {Math.max(0, FLEA_TOTAL_SLOTS - filledSlots)} open · {negotiatingRows.length} negotiating
          </div>
          <div className="mt-3 h-1 bg-zinc-900">
            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (filledSlots / FLEA_TOTAL_SLOTS) * 100)}%` }} />
          </div>
        </Card>
        <Card className="p-5" testId="kpi-flea-rate">
          <div className="overline mb-2">Per-slot rate</div>
          <div className="font-display text-3xl font-bold tabular">₹ 50,000</div>
          <div className="text-xs text-zinc-500 mt-1">{formatLakhs(FLEA_BASE_LAKHS)} list price</div>
        </Card>
      </div>

      <Card className="overflow-hidden" testId="flea-table-card">
        <div className="px-6 py-4 border-b border-zinc-900">
          <div className="overline mb-1">Vendors</div>
          <div className="font-display text-lg">{rows.length} entries · {formatLakhs(revenueLakhs)} closed</div>
        </div>
        {err && (
          <div data-testid="flea-error" className="mx-6 my-4 border border-red-500/30 bg-red-500/10 text-red-200 text-sm px-3 py-2">{err}</div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-[#181818]">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-3 font-medium">Vendor</th>
              <th className="px-4 py-3 font-medium">Slots</th>
              <th className="px-4 py-3 font-medium">Rack</th>
              <th className="px-4 py-3 font-medium">Closing ₹L</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Notes</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="border-t border-zinc-900 bg-[#141414]">
                <td className="px-4 py-3">
                  <input autoFocus value={newRow.vendor_name} onChange={(e) => setNewRow({ ...newRow, vendor_name: e.target.value })}
                    placeholder="Vendor or brand name"
                    data-testid="new-vendor-name"
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
                </td>
                <td className="px-4 py-3 tabular">
                  <input type="number" min={1} max={FLEA_TOTAL_SLOTS}
                    value={newRow.slots_taken}
                    onChange={(e) => setNewRow({ ...newRow, slots_taken: Math.max(1, Math.min(FLEA_TOTAL_SLOTS, +e.target.value)) })}
                    className="w-16 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
                </td>
                <td className="px-4 py-3 tabular text-zinc-400 text-sm">{formatLakhs(newRow.slots_taken * FLEA_BASE_LAKHS)}</td>
                <td className="px-4 py-3 tabular">
                  <div className="flex items-center gap-1.5">
                    <input type="number" step="0.05" min={0} max={1000}
                      value={newRow.closing_price_lakhs == null ? "" : newRow.closing_price_lakhs}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") setNewRow({ ...newRow, closing_price_lakhs: null });
                        else setNewRow({ ...newRow, closing_price_lakhs: Math.max(0, parseFloat(v)) });
                      }}
                      placeholder={`Rack: ${newRow.slots_taken * FLEA_BASE_LAKHS}`}
                      title="Closing value in ₹ Lakhs (blank = use rack value)"
                      data-testid="new-flea-closing"
                      className="w-24 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
                    <span className="text-xs text-zinc-500">L</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select value={newRow.status} onChange={(e) => setNewRow({ ...newRow, status: e.target.value })}
                    data-testid="new-flea-status"
                    className="bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input value={newRow.notes} onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={create} data-testid="new-flea-save" className="p-1.5 text-emerald-400 hover:bg-[#1a1a1a]"><Check className="h-4 w-4" /></button>
                    <button onClick={() => { setAdding(false); setNewRow(EMPTY); }} className="p-1.5 text-zinc-400 hover:bg-[#1a1a1a]"><X className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            )}
            {rows.length === 0 && !adding && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-zinc-500 text-sm">
                No vendors yet. Click <span className="text-emerald-400">Add vendor</span> to start.
              </td></tr>
            )}
            {rows.map((r) => (
              <VendorRow
                key={r.id}
                row={r}
                editing={editingId === r.id}
                draft={draft}
                setDraft={setDraft}
                onEdit={() => startEdit(r)}
                onCancel={() => setEditingId(null)}
                onSave={save}
                onDelete={() => remove(r.id)}
              />
            ))}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
